/**
 * useIndustryLegalCases Hook
 * CRUD operations for industry_legal_cases table (Law Firms)
 * Automatically filters by domain (CompanyType)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CompanyType } from '@/types/company';
import type { IndustryLegalCase } from '@/types/industry';
import { useToast } from '@/components/ui/use-toast';

interface UseIndustryLegalCasesOptions {
  domain: CompanyType;
  status?: string | string[];
  client_id?: number;
}

type LegalCaseStatus = 'open' | 'in_progress' | 'closed_won' | 'closed_lost' | 'settled';

interface CreateLegalCaseInput {
  case_number: string;
  client_id: number;
  case_type?: string;
  court?: string;
  status?: LegalCaseStatus;
  filed_date?: string;
  closed_date?: string;
  fee_structure?: string;
  hourly_rate?: number;
  fixed_fee?: number;
  contingency_percentage?: number;
  total_fees_billed?: number;
  total_fees_collected?: number;
  description?: string;
  outcome?: string;
  metadata?: Record<string, any>;
}

interface UpdateLegalCaseInput {
  id: string;
  case_number?: string;
  client_id?: number;
  case_type?: string;
  court?: string;
  status?: LegalCaseStatus;
  filed_date?: string;
  closed_date?: string;
  fee_structure?: string;
  hourly_rate?: number;
  fixed_fee?: number;
  contingency_percentage?: number;
  total_fees_billed?: number;
  total_fees_collected?: number;
  description?: string;
  outcome?: string;
  metadata?: Record<string, any>;
}

export function useIndustryLegalCases(options: UseIndustryLegalCasesOptions) {
  const { domain, status, client_id } = options;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Get current company (assumes user is authenticated and has company)
  const { data: company } = useQuery({
    queryKey: ['current_company'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  // Fetch legal cases
  const {
    data: cases,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['industry_legal_cases', company?.id, domain, status, client_id],
    queryFn: async () => {
      if (!company) return [];

      let query = supabase
        .from('industry_legal_cases')
        .select('*')
        .eq('company_id', company.id)
        .order('created_at', { ascending: false });

      // Optional filters
      if (status) {
        if (Array.isArray(status)) {
          query = query.in('status', status);
        } else {
          query = query.eq('status', status);
        }
      }

      if (client_id) {
        query = query.eq('client_id', client_id);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as IndustryLegalCase[];
    },
    enabled: !!company,
  });

  // Create legal case
  const createLegalCase = useMutation({
    mutationFn: async (input: CreateLegalCaseInput) => {
      if (!company) throw new Error('No company found');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('industry_legal_cases')
        .insert({
          ...input,
          company_id: company.id,
          user_id: user.id,
          total_fees_billed: input.total_fees_billed || 0,
          total_fees_collected: input.total_fees_collected || 0,
          metadata: input.metadata || {},
        })
        .select()
        .single();

      if (error) throw error;
      return data as IndustryLegalCase;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['industry_legal_cases', company?.id, domain],
      });
      toast({
        title: 'Caso criado',
        description: `Caso ${data.case_number} foi criado com sucesso.`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao criar caso',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Update legal case
  const updateLegalCase = useMutation({
    mutationFn: async (input: UpdateLegalCaseInput) => {
      const { id, ...updates } = input;

      const { data, error } = await supabase
        .from('industry_legal_cases')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as IndustryLegalCase;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['industry_legal_cases', company?.id, domain],
      });
      toast({
        title: 'Caso atualizado',
        description: `Caso ${data.case_number} foi atualizado com sucesso.`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao atualizar caso',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Delete legal case
  const deleteLegalCase = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('industry_legal_cases')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['industry_legal_cases', company?.id, domain],
      });
      toast({
        title: 'Caso excluído',
        description: 'O caso foi excluído com sucesso.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao excluir caso',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    legalCases: cases || [],
    cases: cases || [], // Alias for backward compatibility with DashboardPage
    isLoading,
    error,
    refetch,
    createLegalCase: createLegalCase.mutate,
    createLegalCaseAsync: createLegalCase.mutateAsync,
    updateLegalCase: updateLegalCase.mutate,
    updateLegalCaseAsync: updateLegalCase.mutateAsync,
    deleteLegalCase: deleteLegalCase.mutate,
    deleteLegalCaseAsync: deleteLegalCase.mutateAsync,
    isCreating: createLegalCase.isPending,
    isUpdating: updateLegalCase.isPending,
    isDeleting: deleteLegalCase.isPending,
  };
}

// Alias export for backward compatibility
export const useIndustryCases = useIndustryLegalCases;
